/**
 * The photo control for a stand-alone chat: the initials plate the chat will
 * ship with, a button that opens the file picker, and the two lines the upload
 * can produce.
 *
 * ISSUE-946. The New chat dialog used to render a bare native file input
 * under a text label, so the middle of a designed dialog read "Choose File No
 * file chosen" in the browser's own chrome, in the browser's own font. This is
 * the pattern the rest of the app already uses for exactly this problem:
 * `features/editProfile/EditProfilePhotoField.tsx` and
 * `features/poolBanner/PoolBannerField.tsx` both keep a hidden input on a ref
 * and put a real `Button` in front of it. Reused here rather than invented a
 * third time, so all three agree on labels, focus and disabled behavior.
 *
 * Presentational only: the upload itself belongs to `useChatPhotoUpload`, and
 * the caller owns that hook because the caller is what hands the resulting
 * mediaId to `/chat/create-standalone` or `/chat/update`. The Button and the
 * hidden input now live in `ChatPhotoPickerButton`, which the Members
 * dialog's `Change photo` renders too, so both surfaces share one accept
 * list and one reset.
 */
import { InlineBanner } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { PHOTO_UPLOAD_FAILED_MESSAGE } from "../editProfile/editProfileLogic";
import { initialsFromTitle } from "./chatDisplay";
import { ChatPhotoPickerButton } from "./ChatPhotoPickerButton";
import type { typeChatPhotoUploadState } from "./useChatPhotoUpload";

export type typeChatPhotoFieldProps = {
  /** The chat's name so far. Drives the initials plate, exactly as the list row does. */
  title: string;
  state: typeChatPhotoUploadState;
  onSelectPhoto: (file: File) => void;
  /** Prefix for this field's test ids, so two of these can coexist on one screen. */
  testId: string;
  addLabel: string;
  changeLabel: string;
  uploadingLabel: string;
  pendingMessage: string;
};

export const ChatPhotoField = ({
  title,
  state,
  onSelectPhoto,
  testId,
  addLabel,
  changeLabel,
  uploadingLabel,
  pendingMessage,
}: typeChatPhotoFieldProps): ReactNode => {
  // `useChatPhotoUpload` stops at /upload/complete and hands back a mediaId,
  // not a URL. The preview is therefore the framed file the fan confirmed in
  // the crop editor (ISSUE-1094), drawn from a local object URL and shown only
  // once the upload is ready. Until then the initials plate stays up, and the
  // button label still tells a fan the upload landed.
  const hasPhoto = state.kind === "ready";
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (previewUrl !== null) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  return (
    <div className="flex flex-col items-center gap-2" data-testid={testId}>
      {hasPhoto && previewUrl !== null ? (
        <img
          src={previewUrl}
          alt=""
          className="h-16 w-16 shrink-0 rounded-full object-cover"
          data-testid={`${testId}-preview`}
        />
      ) : (
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary-solid text-h2 text-on-primary">
          {initialsFromTitle(title)}
        </span>
      )}
      <ChatPhotoPickerButton
        label={hasPhoto ? changeLabel : addLabel}
        isUploading={state.kind === "uploading"}
        uploadingLabel={uploadingLabel}
        onSelectPhoto={(file) => {
          setPreviewUrl(URL.createObjectURL(file));
          onSelectPhoto(file);
        }}
        testId={testId}
        size="sm"
      />
      {state.kind === "pendingReview" ? (
        <p className="text-caption text-ink-secondary">{pendingMessage}</p>
      ) : null}
      {state.kind === "failed" ? (
        <div data-testid={`${testId}-failed`}>
          <InlineBanner
            variant="warning"
            message={state.message ?? PHOTO_UPLOAD_FAILED_MESSAGE}
          />
        </div>
      ) : null}
    </div>
  );
};
